import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanBarcode, Search, CameraOff } from "lucide-react";

const READER_ID = "gudang-barcode-reader";

// Dialog pindai ringan untuk slide Stok Gudang: kamera + input manual, hasil dikirim via onDetected(text)
export function GudangScanDialog({ open, onOpenChange, onDetected }) {
  const [manual, setManual] = useState("");
  const [cameraError, setCameraError] = useState("");
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  const kirim = (text) => {
    if (busyRef.current || !text) return;
    busyRef.current = true;
    onDetected(text.trim());
    setTimeout(() => { busyRef.current = false; }, 1500);
  };

  useEffect(() => {
    if (!open) return;
    setManual("");
    setCameraError("");
    let scanner;
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        await new Promise((r) => setTimeout(r, 300)); // tunggu elemen render
        if (cancelled) return;
        scanner = new Html5Qrcode(READER_ID);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 140 } },
          (text) => kirim(text),
          () => {},
        );
      } catch {
        setCameraError("Kamera tidak tersedia atau izin ditolak. Ketik barcode/kode secara manual di bawah.");
      }
    })();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) s.stop().then(() => s.clear()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) kirim(manual.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="gudang-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanBarcode className="h-5 w-5 text-emerald-600" /> Pindai Barcode Barang
          </DialogTitle>
          <DialogDescription>
            Arahkan kamera ke barcode barang untuk langsung menemukan kartunya di slide Stok Gudang.
          </DialogDescription>
        </DialogHeader>

        {cameraError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-800"
            data-testid="gudang-scan-camera-error">
            <CameraOff className="h-4 w-4 shrink-0" /> {cameraError}
          </div>
        ) : (
          <div id={READER_ID} className="rounded-lg overflow-hidden bg-slate-900 min-h-[200px]"
            data-testid="gudang-scan-camera-region" />
        )}

        <form onSubmit={submitManual} className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="Ketik barcode / kode barang (mis. 0001)"
            className="h-10" data-testid="gudang-scan-manual-input" />
          <Button type="submit" variant="outline" disabled={!manual.trim()}
            className="h-10 gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="gudang-scan-manual-button">
            <Search className="h-4 w-4" /> Cari
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
